
"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
  SheetTrigger
} from "@/components/ui/sheet";
import { Logo } from "../logo";
import { cn } from "@/lib/utils";
import { navLinks } from "@/lib/site-config";
import { Menu } from "lucide-react";

export function Header() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={cn(
        "fixed w-full z-50 transition-all duration-300 py-4 px-6 md:px-12",
        scrolled 
          ? "bg-background/90 backdrop-blur-md border-b border-border shadow-lg" 
          : "bg-transparent border-b border-transparent"
      )}>
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <Link href="/" className="flex items-center gap-2 cursor-pointer group">
          <Logo className="h-14 w-auto" />
        </Link>

        <nav className={cn(
          "hidden md:flex items-center space-x-8 text-sm font-medium",
          scrolled ? "text-slate-600 dark:text-slate-300" : "text-slate-100"
        )}>
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "transition-colors relative group py-2",
                  !scrolled && "hover:text-white",
                  scrolled && "hover:text-primary",
                  isActive && (scrolled ? "text-primary" : "text-white font-bold")
                )}
              >
                {link.label}
                <span className={cn(
                  "absolute -bottom-1 left-0 h-0.5 transition-all duration-300",
                  isActive ? "w-full" : "w-0 group-hover:w-full",
                  isActive && !scrolled ? "bg-white" : "bg-primary"
                )}></span>
              </Link>
            )
          })}
        </nav>

        <div className="flex items-center">
          <div className="hidden md:block">
            <Link href="/contact">
              <button className={cn(
                "px-6 py-2.5 rounded-full text-sm font-bold transition-all transform hover:-translate-y-1 hover:scale-105 active:scale-95 border",
                scrolled
                  ? "bg-dark-navy text-white border-transparent hover:border-white/50"
                  : "bg-slate-100 text-slate-900 border-slate-200 hover:bg-slate-200"
              )}>
                Get Free Quotation
              </button>
            </Link>
          </div>

          {isClient && (
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <button aria-label="Open menu" className={cn("md:hidden p-2", scrolled ? "text-slate-800 dark:text-white" : "text-white")}>
                  <Menu className="h-8 w-8" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-background dark:bg-dark-navy p-6">
                <SheetHeader>
                  <SheetTitle className="sr-only">Mobile Menu</SheetTitle>
                  <SheetDescription className="sr-only">A menu of navigation links for the DarshniSoftware website.</SheetDescription>
                </SheetHeader>
                <div className="flex flex-col h-full">
                  <div className="pb-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <Link href="/" onClick={() => setIsSheetOpen(false)} className="flex items-center gap-2 cursor-pointer group">
                      <Logo className="h-10 w-auto" />
                    </Link>
                  </div>

                  <div className="flex flex-col space-y-4 mt-6">
                    {navLinks.map((link) => {
                      const isActive = pathname === link.href;
                      return (
                        <Link
                          key={link.href}
                          href={link.href}
                          className={cn(
                            "text-lg font-medium text-slate-600 dark:text-slate-300 transition-colors hover:text-primary",
                            isActive && "text-primary font-bold"
                          )}
                          onClick={() => setIsSheetOpen(false)}
                        >
                          {link.label}
                        </Link>
                      )
                    })}
                  </div>
                  <div className="mt-auto pt-6">
                    <Link href="/contact" onClick={() => setIsSheetOpen(false)}>
                      <button className="w-full bg-dark-navy text-white px-6 py-3 rounded-full text-base font-bold transition-all transform hover:scale-105 active:scale-95">
                        Get Free Quotation
                      </button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
}
